import type { ReportFilters, ReportOptions, ReportType } from "@crewledger/shared/schemas";
import { apiFetch, downloadAuthenticatedFile } from "@/lib/api";

export type ReportQuery = Partial<Omit<ReportFilters, "page" | "pageSize">> & {
  page?: number;
  pageSize?: number;
};

function buildSearchParams(params?: ReportQuery): URLSearchParams {
  const search = new URLSearchParams();
  if (!params) return search;
  if (params.page) search.set("page", String(params.page));
  if (params.pageSize) search.set("pageSize", String(params.pageSize));
  if (params.payPeriodId) search.set("payPeriodId", params.payPeriodId);
  if (params.siteId) search.set("siteId", params.siteId);
  if (params.workerId) search.set("workerId", params.workerId);
  if (params.crew) search.set("crew", params.crew);
  if (params.equipmentId) search.set("equipmentId", params.equipmentId);
  if (params.jobCode) search.set("jobCode", params.jobCode);
  if (params.costCode) search.set("costCode", params.costCode);
  if (params.dateFrom) search.set("dateFrom", params.dateFrom);
  if (params.dateTo) search.set("dateTo", params.dateTo);
  return search;
}

export async function fetchReportOptions() {
  return apiFetch<{ data: ReportOptions }>("/api/reports/options");
}

export async function fetchReport(reportType: ReportType, params?: ReportQuery) {
  const qs = buildSearchParams(params).toString();
  return apiFetch<{
    data: unknown[];
    meta: {
      total?: number;
      page?: number;
      pageSize?: number;
      canViewPayroll?: boolean;
    };
  }>(`/api/reports/${reportType}${qs ? `?${qs}` : ""}`);
}

export function buildReportExportUrl(reportType: ReportType, params?: ReportQuery): string {
  const search = buildSearchParams(params);
  const qs = search.toString();
  return `/api/reports/${reportType}/export${qs ? `?${qs}` : ""}`;
}

function reportExportFallbackName(reportType: ReportType): string {
  switch (reportType) {
    case "payroll-summary":
      return "werkio-payroll-export.csv";
    case "site-cost":
      return "werkio-site-cost-report.csv";
    case "equipment-usage":
      return "werkio-equipment-usage.csv";
    default:
      return `werkio-${reportType}.csv`;
  }
}

export async function downloadReportCsv(
  reportType: ReportType,
  params?: ReportQuery,
): Promise<void> {
  await downloadAuthenticatedFile(
    buildReportExportUrl(reportType, params),
    reportExportFallbackName(reportType),
  );
}
